import { useMemo, useState } from "react";
import { ChevronRight, MessageCircle, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isSupportCaseTerminal, type SupportFormField } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { isSupportCaseUnread } from "../lib/supportCaseSeen";
import { toast } from "../stores/toast";
import { Badge, Button, EmptyState, PageLoader, statusColor } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";
import {
  SupportRequestWizard,
  type WizardCategory,
  type WizardFormDto,
  type WizardPhase,
} from "../components/support/SupportRequestWizard";

const OTHER_CATEGORY_ID = "__other__";

interface CaseDto {
  id: string;
  caseNo: string;
  kind: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  form: { name: string } | null;
  _count: { comments: number };
}

interface FormCategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  subCategories: { id: string; name: string; slug: string; parentId: string }[];
}

interface FormDto {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  workflowKey: string;
  active: boolean;
  subCategoryId: string | null;
  fields: SupportFormField[];
  subCategory: {
    id: string;
    name: string;
    category: { id: string; name: string } | null;
  } | null;
}

type CaseTab = "open" | "closed";

const KIND_LABEL: Record<string, string> = {
  refund: "Refund request",
  not_delivered: "Order not delivered",
  general: "General enquiry",
  custom: "Request",
};

function isFieldEmpty(value: string | undefined): boolean {
  return value === undefined || value.trim() === "";
}

function caseHasUnread(c: CaseDto): boolean {
  return isSupportCaseUnread(c.id, c.updatedAt, c._count.comments);
}

export function Support() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [phase, setPhase] = useState<WizardPhase>("category");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string | null>(null);
  const [activeForm, setActiveForm] = useState<WizardFormDto | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [caseTab, setCaseTab] = useState<CaseTab>("open");

  const { data: cases, isLoading } = useQuery({
    queryKey: ["cases"],
    queryFn: () => api<{ items: CaseDto[] }>("/ai/cases/mine"),
    refetchInterval: 15_000,
  });

  const { data: forms } = useQuery({
    queryKey: ["support-forms"],
    queryFn: () => api<{ items: FormDto[] }>("/ai/forms"),
  });

  const { data: categoryTree } = useQuery({
    queryKey: ["support-form-categories"],
    queryFn: () => api<{ items: FormCategoryTreeNode[] }>("/ai/form-categories"),
  });

  const activeForms = useMemo(() => (forms?.items ?? []).filter((f) => f.active), [forms?.items]);

  const formsBySubId = useMemo(() => {
    const map = new Map<string, WizardFormDto[]>();
    for (const form of activeForms) {
      if (!form.subCategoryId) continue;
      const list = map.get(form.subCategoryId) ?? [];
      list.push(form);
      map.set(form.subCategoryId, list);
    }
    return map;
  }, [activeForms]);

  const otherForms = useMemo(
    () => activeForms.filter((f) => !f.subCategoryId || !f.subCategory?.category),
    [activeForms],
  );

  const wizardCategories: WizardCategory[] = useMemo(() => {
    const cats: WizardCategory[] = [];
    for (const cat of categoryTree?.items ?? []) {
      const subs = cat.subCategories
        .map((sub) => ({
          id: sub.id,
          name: sub.name,
          formCount: formsBySubId.get(sub.id)?.length ?? 0,
        }))
        .filter((s) => s.formCount > 0);
      if (subs.length) cats.push({ id: cat.id, name: cat.name, subCategories: subs });
    }
    return cats;
  }, [categoryTree?.items, formsBySubId]);

  const formsInSub = useMemo(() => {
    if (selectedCategoryId === OTHER_CATEGORY_ID) return otherForms;
    if (!selectedSubCategoryId) return [];
    return formsBySubId.get(selectedSubCategoryId) ?? [];
  }, [selectedCategoryId, selectedSubCategoryId, formsBySubId, otherForms]);

  const filteredCases = useMemo(() => {
    const items = cases?.items ?? [];
    return items.filter((c) => {
      const closed = isSupportCaseTerminal(c.status);
      return caseTab === "closed" ? closed : !closed;
    });
  }, [cases?.items, caseTab]);

  const sortedCases = useMemo(() => {
    return [...filteredCases].sort((a, b) => {
      const unreadA = caseHasUnread(a);
      const unreadB = caseHasUnread(b);
      if (unreadA !== unreadB) return unreadA ? -1 : 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [filteredCases]);

  const canStartRequest = wizardCategories.length > 0 || otherForms.length > 0;

  if (isLoading) return <PageLoader />;

  function resetWizard() {
    setWizardOpen(false);
    setPhase("category");
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setActiveForm(null);
    setFormValues({});
  }

  function startWizard() {
    if (!canStartRequest) {
      toast("error", "No request forms available");
      return;
    }
    setPhase("category");
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setActiveForm(null);
    setFormValues({});
    setWizardOpen(true);
  }

  function onSelectCategory(categoryId: string) {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    setActiveForm(null);
    setPhase("subcategory");
  }

  function onSelectOther() {
    setSelectedCategoryId(OTHER_CATEGORY_ID);
    setSelectedSubCategoryId(OTHER_CATEGORY_ID);
    const list = otherForms;
    if (list.length === 1) {
      setActiveForm(list[0]!);
      setFormValues({});
      setPhase("fillForm");
    } else {
      setPhase("pickForm");
    }
  }

  function onSelectSubCategory(subCategoryId: string) {
    setSelectedSubCategoryId(subCategoryId);
    const list = formsBySubId.get(subCategoryId) ?? [];
    if (list.length === 1) {
      setActiveForm(list[0]!);
      setFormValues({});
      setPhase("fillForm");
    } else if (list.length > 1) {
      setActiveForm(null);
      setPhase("pickForm");
    }
  }

  function onSelectForm(form: WizardFormDto) {
    setActiveForm(form);
    setFormValues({});
    setPhase("fillForm");
  }

  function wizardBack() {
    if (phase === "fillForm") {
      const list = formsInSub;
      if (list.length > 1) {
        setPhase("pickForm");
        setActiveForm(null);
        return;
      }
      if (selectedCategoryId === OTHER_CATEGORY_ID) {
        setPhase("category");
        setSelectedCategoryId(null);
        setSelectedSubCategoryId(null);
        return;
      }
      setPhase("subcategory");
      setActiveForm(null);
      return;
    }
    if (phase === "pickForm") {
      if (selectedCategoryId === OTHER_CATEGORY_ID) {
        setPhase("category");
        setSelectedCategoryId(null);
        setSelectedSubCategoryId(null);
        return;
      }
      setPhase("subcategory");
      setSelectedSubCategoryId(null);
      return;
    }
    if (phase === "subcategory") {
      setPhase("category");
      setSelectedCategoryId(null);
    }
  }

  async function submitForm() {
    if (!activeForm) return;
    for (const field of activeForm.fields) {
      if (field.required && isFieldEmpty(formValues[field.key])) {
        toast("error", `${field.label} is required`);
        return;
      }
    }
    setBusy(true);
    try {
      const res = await api<{ caseNo: string; id: string }>(`/ai/forms/${activeForm.slug}/submit`, {
        body: { data: formValues },
      });
      toast("success", `Case ${res.caseNo} created`);
      resetWizard();
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      navigate(`/support/cases/${res.id}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't submit");
    } finally {
      setBusy(false);
    }
  }

  const openCount = (cases?.items ?? []).filter((c) => !isSupportCaseTerminal(c.status)).length;
  const closedCount = (cases?.items ?? []).filter((c) => isSupportCaseTerminal(c.status)).length;

  return (
    <div className="space-y-5 px-4 pt-4 pb-6">
      <PageHeader
        title="Support"
        description="Start a structured request or continue an existing conversation with our team."
      />

      <section className="space-y-3">
        <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
          New request
        </h2>
        {!wizardOpen ? (
          <Button className="w-full gap-2" onClick={startWizard} disabled={!canStartRequest}>
            <Plus size={16} />
            Start new request
          </Button>
        ) : (
          <SupportRequestWizard
            phase={phase}
            categories={wizardCategories}
            otherFormCount={otherForms.length}
            selectedCategoryId={selectedCategoryId}
            selectedSubCategoryId={selectedSubCategoryId}
            formsInSub={formsInSub}
            activeForm={activeForm}
            formValues={formValues}
            busy={busy}
            onClose={resetWizard}
            onBack={wizardBack}
            onSelectCategory={onSelectCategory}
            onSelectOther={onSelectOther}
            onSelectSubCategory={onSelectSubCategory}
            onSelectForm={onSelectForm}
            onFormValueChange={(key, value) => setFormValues({ ...formValues, [key]: value })}
            onSubmit={submitForm}
          />
        )}
        {!canStartRequest && !wizardOpen && (
          <p className="text-xs text-white/40">No request forms configured yet.</p>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">My cases</h2>

        <div className="flex gap-2">
          {(
            [
              { id: "open" as const, label: "Open", count: openCount },
              { id: "closed" as const, label: "Closed", count: closedCount },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setCaseTab(tab.id)}
              className={`relative flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                caseTab === tab.id ? "bg-gradient-brand text-white" : "border border-white/10 bg-surface text-white/50"
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span
                  className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                    caseTab === tab.id ? "bg-white/20 text-white" : "bg-white/10 text-white/70"
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {!sortedCases.length ? (
          <EmptyState
            icon={MessageCircle}
            title={caseTab === "open" ? "No open cases" : "No closed cases"}
            hint={
              caseTab === "open"
                ? "Start a new request when you need help."
                : "Resolved and rejected cases appear here."
            }
          />
        ) : (
          <div className="space-y-2">
            {sortedCases.map((c) => {
              const unread = caseHasUnread(c);
              return (
                <Link
                  key={c.id}
                  to={`/support/cases/${c.id}`}
                  className={`flex items-center justify-between rounded-2xl border p-3.5 transition-colors active:border-brand-pink/30 ${
                    unread ? "border-brand-pink/35 bg-brand-pink/[0.06]" : "border-white/5 bg-surface"
                  }`}
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-2">
                      {unread && (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-brand-pink shadow-[0_0_8px_rgba(236,72,153,0.6)]"
                          aria-label="New activity"
                        />
                      )}
                      <p className="truncate text-xs font-semibold">
                        {c.form?.name ?? KIND_LABEL[c.kind] ?? c.kind}
                      </p>
                    </div>
                    <p className="text-[10px] text-white/40">
                      {c.caseNo} · {fmtDate(c.updatedAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge color={statusColor(c.status)}>{c.status.replace(/_/g, " ")}</Badge>
                    <ChevronRight size={16} className="text-white/30" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
